/**
 * Alternate pin positions for the .pocketgolf v2 format.
 *
 * A v2 hole may carry a `pinPositions` array alongside its `grid`: `{ x, y }` cells the cup may be cut
 * to instead of the one the grid draws. The game picks one at random when a round starts, moves the cup
 * there — grid marker and all — and plays the hole exactly as it would any other. So one green can be
 * three or four different approach shots depending on the day, without three or four copies of the hole.
 *
 * ## Why the grid still has exactly one cup
 *
 * The `0` in the grid is what every reader means by "the hole": the flag, the camera, the TO HOLE
 * readout, the cup-capture radius and the import validation all follow it. Painting three of them would
 * give a hole three flags a ball could drop into, which is a different game — and the editor already
 * warns about a second cup for that reason. The alternates are declared beside the grid instead, and the
 * one cup *moves* to whichever is drawn.
 *
 * That is also why the cup itself is never stored here: it is already a candidate by virtue of being
 * painted, and storing it twice would make that flag likelier than the others for no visible reason.
 *
 * The game's copy of these rules is `Level.pinPositions` and `Level.cut(to:)` in
 * Sources/PocketGolfCore/Course.swift. The two must agree.
 */

import { gridWidth } from './grid.js';

/** Where the cup is: the first `0` in the grid, the way the game reads it. `null` if unpainted. */
export function cupCell(grid) {
	for (let y = 0; y < grid.length; y++) {
		const x = (grid[y] || '').indexOf('0');
		if (x >= 0) return { x, y };
	}
	return null;
}

/**
 * The alternates a hole really has, in the order they were declared.
 *
 * Forgiving in the same places the rest of the format is — junk, coordinates off the grid and repeats are
 * dropped rather than rejected — with one addition: the cup's own cell is dropped too, so a file that
 * lists it among its alternates does not end up weighting it. The game dedupes as well, and this keeps
 * the editor honest about what it is about to write.
 */
export function normalisePins(pins, grid) {
	if (!Array.isArray(pins)) return [];

	const cup = cupCell(grid || []);
	const rows = (grid || []).length;
	const columns = gridWidth(grid || []);
	const seen = new Set();
	const kept = [];

	pins.forEach((pin) => {
		if (!pin || typeof pin !== 'object') return;
		const x = Math.trunc(Number(pin.x));
		const y = Math.trunc(Number(pin.y));
		if (!Number.isFinite(x) || !Number.isFinite(y)) return;
		if (x < 0 || x >= columns || y < 0 || y >= rows) return;
		if (cup && cup.x === x && cup.y === y) return;

		const key = `${x},${y}`;
		if (seen.has(key)) return;
		seen.add(key);
		kept.push({ x, y });
	});

	return kept;
}

/** Whether a cell is one of this hole's alternates. */
export function hasPinAt(pins, x, y) {
	return normalisePinList(pins).some((pin) => pin.x === x && pin.y === y);
}

/**
 * The list with `(x, y)` added if it was missing and removed if it was there — what a click on the pin
 * tool does.
 *
 * Not normalised against the grid here, because toggling is an edit and normalising is a save: a pin the
 * author has just placed on a cell they are about to paint green should survive the round trip through
 * this function. `normalisePins` is what runs at the boundaries.
 */
export function togglePin(pins, x, y) {
	const list = normalisePinList(pins);
	const existing = list.findIndex((pin) => pin.x === x && pin.y === y);
	if (existing >= 0) return list.filter((_, index) => index !== existing);
	return [...list, { x, y }];
}

/** Whatever was stored, as a list of integer cells. Defensive: the field is hand-editable. */
function normalisePinList(pins) {
	if (!Array.isArray(pins)) return [];
	return pins
		.filter((pin) => pin && typeof pin === 'object')
		.map((pin) => ({ x: Math.trunc(Number(pin.x)), y: Math.trunc(Number(pin.y)) }))
		.filter((pin) => Number.isFinite(pin.x) && Number.isFinite(pin.y));
}
